/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { getSubmitButtonOptions, SubmitButtonProps } from '@rjsf/utils';
import { Button } from 'primereact/button';
import { FileUpload, FileUploadSelectEvent } from 'primereact/fileupload';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'primereact/tooltip';
import { Job } from 'src/types/processing-schemas';

interface Props extends SubmitButtonProps {
  onReset: () => void;
  onImportOGCJob: (job: Job) => void;
}
export const SubmitButton: React.FC<Props> = (Props) => {
  const { t } = useTranslation();

  const { uiSchema, onReset, onImportOGCJob } = Props;
  const { norender, props } = getSubmitButtonOptions(uiSchema);
  const fileUploadRef = React.useRef<FileUpload>(null);

  if (norender) {
    return null;
  }

  const handleOnUpload = async (event: FileUploadSelectEvent) => {
    const file = event.files[0];
    if (!file) {
      return;
    }

    // Ensure it's JSON
    if (file.type !== 'application/json') {
      alert('Please upload a JSON file.');
      return;
    }

    const content = await file.text();
    const OGCJob: Job = JSON.parse(content);
    onImportOGCJob(OGCJob);
  };

  return (
    <div className="flex align-items-center">
      <Button {...props} size="small" severity="success" type="submit" label={t(['actions.submit', 'Submit'])} />

      <Tooltip className="text-sm" target=".import-input" />
      <FileUpload
        ref={fileUploadRef}
        className="text-sm ml-4 import-input"
        mode="basic"
        name="ogcInputs"
        accept="*.json"
        data-pr-tooltip={t([
          'misc.tooltip_import_inputs',
          "Automatically reuse the previous job's inputs to rerun it without re-entering parameters."
        ])}
        data-pr-position="bottom"
        pt={{
          basicButton: {
            style: {
              height: '37.69px'
            },
            className: 'text-sm p-button p-button-primary '
          }
        }}
        chooseOptions={{
          icon: 'pi pi-upload'
        }}
        chooseLabel="Import inputs"
        customUpload
        onSelect={handleOnUpload}
        cancelLabel="cancel"
      />

      <Button
        {...props}
        size="small"
        type="reset"
        severity="secondary"
        onClick={() => {
          fileUploadRef.current?.clear();
          onReset();
        }}
        className="ml-4"
        label={t(['actions.reset', 'Reset'])}
      />
    </div>
  );
};
